import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Form, Input, Button, Row, Col,
} from 'antd';
import { parse } from 'qs';
import { withRouter, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { uniq } from 'ramda';

import { validatePassword } from '../../actions/account';
import { smoothScroll } from '../../helpers/view';
import { upperAndLowerCaseValidator } from '../../helpers/validators';

import PasswordRules from '../PasswordRules';
import c from '../../containers/Profile/Profile.less';

const FormItem = Form.Item;

const layout = {
  left: {
    xs: 24,
    md: 7,
  },
  right: {
    xs: 24,
    md: 17,
  },
  offset: {
    xs: {
      offset: 0,
    },
    md: {
      offset: 7,
    },
  },
};

const passwordRules = [{
  required: true, validator: upperAndLowerCaseValidator, message: 'Upper & lowercase letters',
}, {
  required: true,
  pattern: /^(?=.*[0-9`~!@#$%^&*()\-_=+[{\]}\\|;:'"<,>./?]).*$/,
  message: 'At least one digit or special symbol',
}, {
  required: true, min: 6, message: '6 or more characters',
}];

function hasErrors(fieldsError) {
  return Object.keys(fieldsError).some((field) => fieldsError[field]);
}

class ChangePasswordFormComponent extends Component {
  static propTypes = {
    form: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
    validatePassword: PropTypes.func.isRequired,
    showCurrent: PropTypes.bool,
  };

  static defaultProps = {
    showCurrent: true,
  };

  state = {
    passwordServerErrors: null,
    confirmDirty: false,
  };

  componentDidMount() {
    this.props.form.validateFields();
  }

  get passwordServerErrors() {
    const { form: { getFieldError } } = this.props;

    return typeof getFieldError('newPassword') !== 'undefined' && this.state.passwordServerErrors;
  }

  getFieldStatus = (field) => {
    const { form: { isFieldTouched } } = this.props;

    if (this.getFieldErrors(field)) {
      return 'error';
    }
    if (isFieldTouched(field)) {
      return 'success';
    }

    return '';
  };

  getFieldErrors = (field) => {
    const { form: { getFieldError, isFieldTouched } } = this.props;

    return isFieldTouched(field) && getFieldError(field);
  };

  setFormErrors = (errors = {}) => {
    const { form } = this.props;
    const formValues = form.getFieldsValue();
    const formErrors = form.getFieldsError();

    form.setFields(Object.keys(formValues).reduce((acc, key) => {
      const fieldErrors = uniq([
        ...(formErrors[key] || []),
        ...(errors[key] || []),
      ]);

      if (fieldErrors.length > 0) {
        acc[key] = {
          value: formValues[key],
          dirty: true,
          touched: form.isFieldTouched(key),
          errors: fieldErrors.map((err) => new Error(err)),
        };
      }

      return acc;
    }, {}));

    smoothScroll();
  };

  compareToFirstPassword = (rule, value, callback) => {
    const { form } = this.props;
    if (value && value !== form.getFieldValue('newPassword')) {
      callback('Passwords do not match');
    } else {
      callback();
    }
  };

  validateToNextPassword = (rule, value, callback) => {
    const { form } = this.props;
    if (value && this.state.confirmDirty) {
      form.validateFields(['confirmPassword'], { force: true });
    }
    callback();
  };

  handleConfirmBlur = (e) => {
    const { value } = e.target;
    this.setState((state) => ({
      confirmDirty: state.confirmDirty || !!value,
    }));
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { form, location: { search } } = this.props;
    const { token } = parse(search.slice(1));
    const formValues = form.getFieldsValue();

    this.props.validatePassword(formValues, token)
      .then(() => {
        this.setFormErrors();

        return this.props.onSubmit(formValues)
          .then(() => {
            form.resetFields();
          });
      })
      .catch((err) => {
        if (err.body && err.body.newPassword) {
          this.setState({
            passwordServerErrors: err.body.newPassword,
          });
        }

        this.setFormErrors(err.body);
      });
  };

  handleNewPasswordChange = () => {
    if (this.state.passwordServerErrors) {
      this.setState({
        passwordServerErrors: null,
      });
    }
  };

  render() {
    const { form: { isFieldTouched, getFieldDecorator, getFieldError }, showCurrent } = this.props;

    return (
      <Form onSubmit={this.handleSubmit} hideRequiredMark>
        {
          showCurrent && (
            <Row>
              <Col {...layout.left} className={c.label}>Current password </Col>
              <Col {...layout.right}>
                <FormItem
                  hasFeedback
                  className={c.input}
                  validateStatus={this.getFieldStatus('currentPassword')}
                  help={this.getFieldErrors('currentPassword') || ''}
                >
                  {getFieldDecorator('currentPassword', {
                    rules: [{
                      required: true,
                      message: 'Please enter your current password',
                    }],
                  })(
                    <Input type="password" />,
                  )}
                </FormItem>
              </Col>
            </Row>
          )
        }
        <Row>
          <Col {...layout.left} className={c.label}>New password </Col>
          <Col {...layout.right}>
            <FormItem
              hasFeedback
              className={c.input}
              validateStatus={this.getFieldStatus('newPassword')}
              help={this.passwordServerErrors}
            >
              {getFieldDecorator('newPassword', {
                rules: [
                  ...passwordRules,
                  {
                    validator: this.validateToNextPassword,
                  },
                ],
              })(
                <Input
                  type="password"
                  onChange={this.handleNewPasswordChange}
                />,
              )}
            </FormItem>
            <PasswordRules
              isDirty={isFieldTouched('newPassword')}
              errors={getFieldError('newPassword')}
              rules={passwordRules.map((rule) => rule.message)}
            />
          </Col>
        </Row>
        <Row>
          <Col {...layout.left} className={c.label}>Confirm password </Col>
          <Col {...layout.right}>
            <FormItem
              hasFeedback
              className={c.input}
              validateStatus={this.getFieldStatus('confirmPassword')}
              help={this.getFieldErrors('confirmPassword') || ''}
            >
              {getFieldDecorator('confirmPassword', {
                validateFirst: true,
                rules: [
                  {
                    required: true,
                    message: 'Please enter your new password',
                  },
                  {
                    validator: this.compareToFirstPassword,
                  }],
              })(
                <Input type="password" onBlur={this.handleConfirmBlur} />,
              )}
            </FormItem>
          </Col>
        </Row>
        <Row>
          <Col {...layout.offset} className={c.actions}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className={c.submitProfile}
              disabled={hasErrors(this.props.form.getFieldsError())}
            >
              Change password
            </Button>
            <Link to="/shop/profile" className={c.cancel}>Cancel</Link>
          </Col>
        </Row>
      </Form>
    );
  }
}

const mapDispatchToProps = {
  validatePassword,
};

export default connect(null, mapDispatchToProps)(withRouter(Form.create()(ChangePasswordFormComponent)));
